import {
	BeforeInsert,
	BeforeUpdate,
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { normalizePhoneToE164 } from '../../utils/validation';
import { BaseEntity } from './base.entity';
import { CompanyEntity } from './company.entity';
import { PurchaseOrderEntity } from './purchase-order.entity';
import { VendorAddressEntity } from './vendor-address.entity';
import { VendorItemEntity } from './vendor-item.entity';
import { VendorPhoneEntity } from './vendor-phone.entity';

@Index('fk_company__id', ['fkCompanyId'], {})
@Index('vendor_externalId_uindex', ['deletedUserId', 'externalId'], {
	unique: true,
})
@Entity('vendor')
export class VendorEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', length: 255 })
	public externalId!: string;

	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('varchar', { name: 'address1', nullable: true, length: 255 })
	public address1!: string | null;

	@Column('varchar', { name: 'address2', nullable: true, length: 255 })
	public address2!: string | null;

	@Column('varchar', { name: 'city', nullable: true, length: 255 })
	public city!: string | null;

	@Column('varchar', { name: 'state', nullable: true, length: 255 })
	public state!: string | null;

	@Column('varchar', { name: 'zip', nullable: true, length: 255 })
	public zip!: string | null;

	@Column('varchar', { name: 'phone', nullable: true, length: 255 })
	public phone!: string | null;

	@Column('varchar', { name: 'fax', nullable: true, length: 255 })
	public fax!: string | null;

	@Column('varchar', { name: 'email', nullable: true, length: 255 })
	public email!: string | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyId!: number;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	@OneToMany(
		() => PurchaseOrderEntity,
		(purchaseOrder) => purchaseOrder.fkVendor,
	)
	public purchaseOrders!: Array<PurchaseOrderEntity>;

	@ManyToOne(() => CompanyEntity, (company) => company.vendors, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@OneToMany(
		() => VendorAddressEntity,
		(vendorAddress) => vendorAddress.fkVendor,
	)
	public vendorAddresses!: Array<VendorAddressEntity>;

	@OneToMany(() => VendorItemEntity, (vendorItem) => vendorItem.vendor)
	public vendorItems!: Array<VendorItemEntity>;

	@OneToMany(() => VendorPhoneEntity, (vendorPhone) => vendorPhone.fkVendor)
	public vendorPhones!: Array<VendorPhoneEntity>;

	// Write-path safety net — see matching hook on `ClientEntity`.
	@BeforeInsert()
	@BeforeUpdate()
	// `protected` (not `private`): this hook is invoked by TypeORM via the
	// @BeforeInsert/@BeforeUpdate decorators, not by direct calls, so TS
	// `noUnusedLocals` (TS6133) would wrongly flag a `private` version as unused.
	protected _normalizePhone(): void {
		if (this.phone != null && this.phone !== '') {
			const normalized = normalizePhoneToE164(this.phone);
			if (normalized != null) {
				this.phone = normalized;
			}
		}
	}
}
